/**
 * CSS 3D Voxel Renderer
 *
 * Uses CSS 3D transforms for hardware-accelerated rendering of block models.
 * Implements greedy meshing to minimize the number of DOM elements.
 */

import type { Vec4, Block } from "../types";
import { BlockRenderer } from "./base";

/** Face direction for cubes */
type FaceDirection = "front" | "back" | "top" | "bottom" | "right" | "left";

/** Face data for rendering */
interface FaceData {
    direction: FaceDirection;
    x: number;
    y: number;
    z: number;
    w: number; // width in block units
    h: number; // height in block units
    color: Vec4;
}

// Scale factor to convert World Units (voxels) to CSS Pixels

export class CSSBlockRenderer extends BlockRenderer {
    private container: HTMLElement;
    private scene: HTMLElement;
    private facesContainer: HTMLElement;
    private faces: FaceData[] = [];
    private faceElements: HTMLElement[] = [];
    private styleSheet: HTMLStyleElement;
    protected blocks: Block[] = [];
    private lastRotX = 0;
    private lastRotY = 0;
    private lastZoom = 1;

    constructor(container: HTMLElement, blocks: Block[], modelScale: number) {
        super(blocks, modelScale);
        this.container = container;
        this.styleSheet = this.injectStyles();
        this.scene = this.createScene();
        this.facesContainer = this.createFacesContainer();
        this.scene.appendChild(this.facesContainer);
        this.container.appendChild(this.scene);

        this.setBlocks(blocks);
    }

    /**
     * Inject CSS styles for the 3D scene
     */
    private injectStyles(): HTMLStyleElement {
        const style = document.createElement("style");
        style.textContent = `
      .block-scene {
        position: relative;
        width: 100%;
        height: 100%;
        perspective: 800px;
        perspective-origin: 50% 50%;
        overflow: hidden;
        contain: strict;
      }

      .block-faces-container {
        position: absolute;
        top: 50%;
        left: 50%;
        transform-style: preserve-3d;
        will-change: transform;
      }

      .block-face {
        position: absolute;
        transform-style: preserve-3d;
        backface-visibility: hidden;
        will-change: transform;
        contain: strict;
        box-sizing: border-box;
      }
    `;
        document.head.appendChild(style);
        return style;
    }

    /**
     * Create the 3D scene container
     */
    private createScene(): HTMLElement {
        const scene = document.createElement("div");
        scene.className = "block-scene";
        return scene;
    }

    /**
     * Create the faces container that will be transformed
     */
    private createFacesContainer(): HTMLElement {
        const container = document.createElement("div");
        container.className = "block-faces-container";
        return container;
    }

    /**
     * Set blocks directly for rendering
     */
    public setBlocks(blocks: Block[]): void {
        this.blocks = blocks;
        this.faces = [];
        this.faceElements.forEach(el => el.remove());
        this.faceElements = [];

        if (this.blocks && this.blocks.length > 0) {
            this.buildFaces();
        }

        this.createFaceElements();
    }

    /**
     * Build faces directly from Block definitions
     */
    private buildFaces(): void {
        if (!this.blocks) return;

        this.faces = [];

        this.blocks.forEach(block => {
            // Block center is in block coords.
            // Dimensions are in block units.
            // We need to generate 6 faces for each block.

            const { center, width, height, depth, color } = block;
            const [cx, cy, cz] = center;

            const minX = cx - width / 2;
            const minY = cy - height / 2;
            const minZ = cz - depth / 2;

            // Front
            this.faces.push({
                direction: "front",
                x: minX + 0.5, y: minY + 0.5, z: minZ + depth - 0.5,
                w: width, h: height, color
            });

            // Back
            this.faces.push({
                direction: "back",
                x: minX + 0.5, y: minY + 0.5, z: minZ + 0.5,
                w: width, h: height, color
            });

            // Top (face.x is x, face.z is z, face.y is Y layer)
            this.faces.push({
                direction: "top",
                x: minX + 0.5, y: minY + height - 0.5, z: minZ + 0.5,
                w: width, h: depth, color
            });

            // Bottom
            this.faces.push({
                direction: "bottom",
                x: minX + 0.5, y: minY + 0.5, z: minZ + 0.5,
                w: width, h: depth, color
            });

            // Right (face.x is X layer, face.y is y, face.z is z)
            this.faces.push({
                direction: "right",
                x: minX + width - 0.5, y: minY + 0.5, z: minZ + 0.5,
                w: depth, h: height, color
            });

            // Left
            this.faces.push({
                direction: "left",
                x: minX + 0.5, y: minY + 0.5, z: minZ + 0.5,
                w: depth, h: height, color
            });
        });
    }

    /**
     * Convert color array to CSS rgba string
     */
    private colorToCSS(color: Vec4): string {
        const r = Math.round(color[0] * 255);
        const g = Math.round(color[1] * 255);
        const b = Math.round(color[2] * 255);
        const a = color[3];
        return `rgba(${r}, ${g}, ${b}, ${a})`;
    }

    /**
     * Create DOM elements for all faces
     */
    private createFaceElements(): void {
        const fragment = document.createDocumentFragment();

        this.faces.forEach((face) => {
            const element = document.createElement("div");
            element.className = "block-face";
            element.style.backgroundColor = this.colorToCSS(face.color);
            fragment.appendChild(element);
            this.faceElements.push(element);
        });

        this.facesContainer.appendChild(fragment);
    }

    /**
     * Get the transform for a face based on its direction, position, and dimensions
     */
    private getFaceTransform(
        face: FaceData,
        scale: number
    ): { transform: string; width: number; height: number } {
        // Match WebGL visual scale (fov 45, distance 6, baseScale height/8)
        // unit_in_pixels = height * cot(fov/2) / (2 * distance)
        // VISUAL_FACTOR = (cot(22.5) / 12) * 8 approx 1.6096
        const VISUAL_FACTOR = 1.6096;
        const s = scale * this.modelScale * VISUAL_FACTOR;

        // Face dimensions in pixels
        const pixelW = face.w * s;
        const pixelH = face.h * s;

        // Calculate center offset from the 'start' voxel (face.x, face.y, face.z)
        // If w=1, offset is 0. If w=2, offset is 0.5 * s.
        // Generalized: offset = ((w - 1) * s) / 2

        // Wait, logic check:
        // Voxel 'x' center is at x*s.
        // Merged block starts at x*s (center of first voxel).
        // No, my previous logic was: x*s is center of voxel x.
        // If I have voxels at x=0,1. Centers at 0 and s.
        // Merged face center should be at (0+s)/2 = 0.5s.
        // Start voxel x=0. Center is 0.
        // Offset needed = 0.5s.
        // Formula: (w-1)/2 * s.
        // If w=1: 0. Correct.
        // If w=2: 0.5s. Correct.

        // However, this offset depends on the plane axis.
        // For Front/Back faces (plane XY): width is along X, height along Y.
        // For Top/Bottom faces (plane XZ): width is along X, height along Z.
        // For Right/Left faces (plane ZY): width is along Z, height along Y.

        let dx = 0, dy = 0, dz = 0;

        switch (face.direction) {
            case "front": // w along X, h along Y
            case "back":
                dx = (face.w - 1) / 2 * s;
                dy = -(face.h - 1) / 2 * s; // Y is inverted in CSS
                break;
            case "top": // w along X, h along Z
            case "bottom":
                dx = (face.w - 1) / 2 * s;
                dz = (face.h - 1) / 2 * s;
                break;
            case "right": // w along Z, h along Y
            case "left":
                dz = (face.w - 1) / 2 * s;
                dy = -(face.h - 1) / 2 * s;
                break;
        }

        // Base center of the starting voxel
        const cx = face.x * s + dx;
        const cy = -face.y * s + dy;
        const cz = face.z * s + dz;

        const halfW = pixelW / 2;
        const halfH = pixelH / 2;
        const halfDepth = s / 2; // Depth is always 1 voxel unit for the face offset

        let transform: string;

        switch (face.direction) {
            case "front":
                // Face in XY plane, normal +Z
                transform = `translate3d(${cx - halfW}px, ${cy - halfH}px, ${cz + halfDepth}px)`;
                break;

            case "back":
                // Face in XY plane, normal -Z. rotateY(180)
                transform = `translate3d(${cx - halfW}px, ${cy - halfH}px, ${cz - halfDepth}px) rotateY(180deg)`;
                break;

            case "top":
                // Face in XZ plane, normal +Y (up). rotateX(90)
                // In CSS, rotateX(90) makes Y+ point to -Z?
                // Initial: Z+ toward viewer. Y+ down.
                // rotateX(90): top rotates back. Z+ becomes Y- (up).
                // We want plane flat XZ.

                // Let's re-derive carefully.
                // Start: Rect in XY plane.
                // translate3d places top-left at (tx, ty, tz).
                // rotateX(90):
                //   Top edge (y=0) stays.
                //   Bottom edge (y=s) moves into screen (-z).
                //   Resulting face is in XZ plane?
                //   Normal points UP (-Y in CSS).

                // We want face at Y = cy - halfDepth (top of voxel).
                // We are calculating translation for the element before rotation.
                // Element size: pixelW x pixelH (which maps to X and Z).

                // With rotateX(90):
                // Local H (originally Y) maps to Z depth.
                // We want Z range from [cz - halfH, cz + halfH].
                // We want Y position fixed at cy - halfDepth.

                // translate(tx, ty, tz) -> Center of element is (tx+w/2, ty+h/2, tz)
                // Rotate around center.
                // Center ends up at same geometrical spot (translate + center offset).

                // Center of our desired face in world: (cx, cy - halfDepth, cz).
                // Center of CSS element (before rot): (tx + halfW, ty + halfH, tz).
                // We want these to coincide?
                // Yes, if we rotate around center, center stays put.

                // So:
                // tx = cx - halfW
                // ty = (cy - halfDepth) - halfH  <- Wait.
                // After rotateX(90), the element's local Y axis points along -Z world axis.
                // The element's local center Y relates to world Z.

                // Let's stick to the previous implementation's logic but adapted.
                // Previous: `translate3d(${cx - halfS}px, ${cy - s}px, ${cz}px) rotateX(90deg)`
                // Here 'cx' was voxel center. 'cy-s' was top of voxel.
                // 'cz' was voxel center.

                // New Center: (cx, cy - halfDepth, cz).
                // We want the element center to be here.
                // The element has dimensions pixelW x pixelH.
                // We render it as a div of size pixelW x pixelH.
                // Before rotation, it's flat in XY.
                // We move center to target p.

                transform = `translate3d(${cx - halfW}px, ${cy - halfDepth - halfH}px, ${cz}px) rotateX(90deg)`;
                break;

            case "bottom":
                // Normal +Y (down). rotateX(-90).
                // Target Y = cy + halfDepth.
                // Target Center: (cx, cy + halfDepth, cz)
                transform = `translate3d(${cx - halfW}px, ${cy + halfDepth - halfH}px, ${cz}px) rotateX(-90deg)`;
                break;

            case "right":
                // Normal +X. rotateY(90).
                // Target X = cx + halfDepth.
                // Target Center: (cx + halfDepth, cy, cz)
                // Element dims: pixelW (z), pixelH (y)
                transform = `translate3d(${cx + halfDepth - halfW}px, ${cy - halfH}px, ${cz}px) rotateY(90deg)`;
                break;

            case "left":
                // Normal -X. rotateY(-90).
                // Target X = cx - halfDepth.
                // Target Center: (cx - halfDepth, cy, cz)
                transform = `translate3d(${cx - halfDepth - halfW}px, ${cy - halfH}px, ${cz}px) rotateY(-90deg)`;
                break;
        }

        return { transform, width: pixelW, height: pixelH };
    }

    /**
     * Render the blocks with the given dimensions and rotation
     */
    public render(
        width: number,
        height: number,
        rotX: number,
        rotY: number,
        zoom: number = 1.0
    ): void {
        const baseScale = Math.min(width, height) / 8;
        const scale = baseScale * zoom;

        this.scene.style.perspective = `${Math.max(width, height) * 1.5}px`;

        const rotXDeg = (rotX * 180) / Math.PI;
        // Invert Y rotation to match WebGL behavior (drag left -> rotate left)
        const rotYDeg = (rotY * 180) / Math.PI;

        const rotationChanged =
            Math.abs(rotX - this.lastRotX) > 0.001 ||
            Math.abs(rotY - this.lastRotY) > 0.001 ||
            Math.abs(zoom - this.lastZoom) > 0.001;

        if (rotationChanged) {
            this.lastRotX = rotX;
            this.lastRotY = rotY;
            this.lastZoom = zoom;

            this.facesContainer.style.transform = `rotateX(${-rotXDeg}deg) rotateY(${rotYDeg}deg)`;
            this.updateFaceTransforms(scale);
        }
    }

    /**
     * Update all face transforms with the current scale
     */
    private updateFaceTransforms(scale: number): void {
        const updates: { el: HTMLElement; transform: string; w: number; h: number }[] = [];

        this.faces.forEach((face, i) => {
            const { transform, width, height } = this.getFaceTransform(face, scale);
            updates.push({
                el: this.faceElements[i],
                transform,
                w: width,
                h: height,
            });
        });

        updates.forEach(({ el, transform, w, h }) => {
            el.style.transform = transform;
            el.style.width = `${w}px`;
            el.style.height = `${h}px`;
        });
    }

    /**
     * Clean up renderer resources
     */
    public dispose(): void {
        this.faceElements.forEach((el) => el.remove());
        this.faceElements = [];

        if (this.scene.parentNode) {
            this.scene.parentNode.removeChild(this.scene);
        }

        if (this.styleSheet.parentNode) {
            this.styleSheet.parentNode.removeChild(this.styleSheet);
        }
    }
}
